// Puente con el plugin nativo IkoVault (Capacitor) de la app móvil.
//
// Cuando el usuario abre un IkoPod en la app de móvil, registramos su material
// (wrappingKey + semilla de firma) en el almacén SEGURO nativo (Keystore en
// Android / Keychain de App Group en iOS). Así el adaptador de archivos del
// sistema —DocumentsProvider (Android) / File Provider (iOS)— puede abrir el
// IkoPod y mostrarlo en la app Archivos, como Dropbox.
//
// Server-blind: el material ya vive en la RAM de la app al abrir el Pod; aquí
// solo se persiste cifrado por el sistema, en el sandbox de la propia app. En
// web/escritorio (sin plugin nativo) todo esto es no-op silencioso.

import { Capacitor, registerPlugin } from '@capacitor/core';
import * as crypto from './crypto';

interface IkoVaultPlugin {
  registerPod(opts: { podId: string; name: string; wrappingKey: string; signingSeed: string }): Promise<void>;
  unregisterPod(opts: { podId: string }): Promise<void>;
  listPods(): Promise<{ pods: Array<{ podId: string; name: string }> }>;
  wipeAll(): Promise<void>;
}

const IkoVault = registerPlugin<IkoVaultPlugin>('IkoVault');

/**
 * ¿Corremos dentro de la app nativa (Android/iOS)?
 *
 * IMPORTANTE: NO usamos `isPluginAvailable('IkoVault')`. Cuando la app carga la
 * web desde un `server.url` remoto (app.ikognit.com), Capacitor a veces NO rellena los
 * PluginHeaders de los plugins propios, así que `isPluginAvailable` devuelve
 * false AUNQUE el plugin SÍ esté registrado y la llamada nativa funcione. Eso
 * abortaba el registro del IkoPod en Archivos. Basta con saber que estamos en
 * nativo; si por lo que fuera el plugin no respondiera, el try/catch de cada
 * función lo absorbe sin romper nada.
 */
function nativeAvailable(): boolean {
  return Capacitor.isNativePlatform();
}

/**
 * Registra (o refresca) un IkoPod en el almacén nativo para que aparezca en la
 * app Archivos del sistema. `signingSeed` se recorta a 32 B (la semilla Ed25519),
 * igual que en el conector de escritorio. No-op fuera de la app móvil.
 */
export async function registerPodForFiles(
  podId: string,
  name: string,
  wrappingKey: Uint8Array,
  signingSeed: Uint8Array,
): Promise<void> {
  if (!Capacitor.isNativePlatform()) return;
  try {
    await IkoVault.registerPod({
      podId,
      name,
      wrappingKey: crypto.b64encode(wrappingKey),
      signingSeed: crypto.b64encode(signingSeed.slice(0, 32)),
    });
  } catch {
    // El plugin solo existe en la app nativa; cualquier fallo aquí no debe
    // afectar al uso normal del IkoPod en la propia app.
  }
}

/** Quita un IkoPod del almacén nativo (deja de verse en Archivos). No-op en web. */
export async function unregisterPodFromFiles(podId: string): Promise<void> {
  if (!nativeAvailable()) return;
  try {
    await IkoVault.unregisterPod({ podId });
  } catch {
    /* no-op */
  }
}

/**
 * ZERO-TRACE: desmonta TODOS los IkoPods de Archivos y borra TODO el material
 * del almacén seguro nativo. Se llama al salir del IKO, cerrar sesión o cuando
 * cambia la sesión activa — al salir de un IKO no debe quedar constancia en
 * ningún sitio (ni la ubicación, ni el nombre, ni las claves), y sin material
 * la extensión no puede descifrar nada. No-op en web/escritorio.
 */
export async function wipeAllPodsFromFiles(): Promise<void> {
  if (!nativeAvailable()) return;
  try {
    await IkoVault.wipeAll();
  } catch {
    /* no-op */
  }
}
